// src/data/chartHelp.js
//
// Textes d'aide des graphiques du Journal Auto, affichés par la pastille ⓘ.
//
// Séparés de JournalAutoPage.js à dessein : onze fiches en deux langues
// représentent plus de texte que tout le reste des dictionnaires de la page,
// et ce contenu bouge indépendamment du code.
//
// Les visuels vivent dans `public/help/charts/`. Ils sont chargés en `lazy`,
// donc seulement à l'ouverture de la fiche concernée.

const IMG = "/help/charts";

const fr = {
  pnlCurve: {
    title: "Courbe P&L (clos)",
    intro:
      "Évolution cumulée du résultat de vos trades clôturés, dans l’ordre de fermeture.",
    meaning:
      "La courbe montre la trajectoire du compte plutôt que le détail des trades. Elle répond à une seule question : le capital progresse-t-il, et à quel prix ?",
    howTo:
      "Les paliers correspondent à des séries régulières, les décrochages verticaux à des pertes concentrées. Le sélecteur de période en haut à droite restreint l’affichage à la dernière semaine, au dernier mois, aux 3, 6 ou 12 derniers mois.",
    image: null,
  },

  scoreRadar: {
    title: "Radar de score",
    intro:
      "Vue synthétique de la qualité globale de vos performances selon plusieurs indicateurs clés.",
    meaning:
      "Chaque axe représente une dimension de votre trading : taux de réussite, Profit Factor, espérance, capacité de récupération, drawdown ou séries de résultats. Le graphique permet d’identifier rapidement vos points forts et les aspects à améliorer.",
    howTo:
      "Plus la zone colorée s’étend vers l’extérieur sur un axe, meilleur est votre score sur cet indicateur. Une forme large et équilibrée traduit généralement une performance plus solide et régulière. Une zone proche du centre révèle un indicateur encore fragile.",
    image: `${IMG}/score-radar.png`,
  },

  tracker: {
    title: "Trackeur de progrès",
    intro:
      "Suivi visuel de votre régularité et de vos résultats au fil des jours et des semaines.",
    meaning:
      "Chaque case correspond à une journée. Les couleurs permettent de distinguer rapidement les journées positives, négatives ou sans activité.",
    howTo:
      "Lisez le tableau de gauche à droite pour suivre les semaines et de haut en bas pour consulter les jours. Les cases vertes représentent les journées positives, les rouges les journées négatives et les cases vides les jours sans donnée ou sans trade. L’objectif est d’observer la régularité plutôt qu’une seule journée isolée.",
    image: `${IMG}/progress-tracker.png`,
  },

  avgWinLoss: {
    title: "Moyenne gains / pertes",
    intro:
      "Comparaison entre le montant moyen gagné sur les trades gagnants et le montant moyen perdu sur les trades perdants.",
    meaning:
      "Ce graphique permet de vérifier si vos gains moyens compensent suffisamment vos pertes moyennes. La courbe indique également l’évolution du taux de réussite sur la période.",
    howTo:
      "Les barres vertes représentent les gains moyens et les barres rouges les pertes moyennes. La courbe se lit sur l’axe en pourcentage à droite. Une stratégie peut rester rentable avec un faible taux de réussite lorsque les gains moyens sont nettement supérieurs aux pertes moyennes.",
    image: `${IMG}/avg-win-loss.png`,
  },

  winLossSplit: {
    title: "Répartition gains / pertes",
    intro:
      "Répartition de l’ensemble de vos trades entre positions gagnantes et positions perdantes.",
    meaning:
      "Le graphique présente votre taux de réussite, aussi appelé Win Rate. Il indique la proportion de trades clôturés avec un résultat positif.",
    howTo:
      "La partie verte représente les trades gagnants et la partie rouge les trades perdants. Le pourcentage affiché au centre correspond au taux de réussite. Ce chiffre doit toujours être analysé avec le montant moyen des gains et des pertes : un taux de réussite élevé ne garantit pas forcément une stratégie rentable.",
    image: `${IMG}/win-loss-split.png`,
  },

  monthlyStats: {
    title: "Statistiques mensuelles",
    intro:
      "Évolution de vos résultats et de votre activité de trading mois par mois.",
    meaning:
      "Ce graphique permet de comparer les performances obtenues au cours de chaque mois et d’observer leur relation avec le nombre de trades exécutés.",
    howTo:
      "Les barres représentent le résultat net de chaque mois, tandis que la courbe montre le volume de trades correspondant. Utilisez-le pour déterminer si une hausse de l’activité améliore réellement vos performances ou si elle entraîne davantage d’erreurs.",
    image: `${IMG}/monthly-stats.png`,
  },

  resultDistribution: {
    title: "Distribution des résultats (par trade)",
    intro:
      "Répartition de vos trades selon le montant gagné ou perdu sur chaque opération.",
    meaning:
      "Ce graphique montre les niveaux de résultat les plus fréquents. Il permet notamment de repérer les petites pertes répétitives, les gains habituels et les résultats exceptionnels.",
    howTo:
      "L’axe horizontal représente les différents niveaux de gains ou de pertes, et l’axe vertical le nombre de trades concernés. Les barres rouges correspondent aux résultats négatifs et les vertes aux résultats positifs. Une forte concentration autour d’une valeur indique que de nombreux trades produisent un résultat similaire.",
    image: `${IMG}/result-distribution.png`,
  },

  pnlDayHour: {
    title: "P&L par jour et heure",
    intro:
      "Carte de chaleur présentant vos performances selon le jour de la semaine et l’heure de trading.",
    meaning:
      "Elle permet d’identifier les créneaux pendant lesquels vous obtenez vos meilleurs et vos moins bons résultats.",
    howTo:
      "Chaque case représente la performance moyenne enregistrée pour un jour et une heure précis. Les couleurs positives signalent les créneaux rentables, tandis que les couleurs négatives indiquent les périodes moins performantes. Plus la couleur est intense, plus le résultat moyen est important.",
    image: `${IMG}/pnl-day-hour.png`,
  },

  durationVsPnl: {
    title: "Durée des trades vs performance",
    intro:
      "Analyse de la relation entre la durée de vos positions et leur résultat financier.",
    meaning:
      "Ce graphique permet de déterminer si vos trades courts, moyens ou longs sont généralement les plus performants. Il aide également à repérer les positions conservées trop longtemps.",
    howTo:
      "Chaque point représente un trade. Sa position horizontale indique sa durée et sa position verticale son gain ou sa perte. Les points situés au-dessus de zéro sont gagnants, tandis que ceux situés en dessous sont perdants. Le coefficient r indique la force de la relation entre la durée et la performance : une valeur proche de zéro signifie qu’il existe peu de corrélation.",
    image: `${IMG}/duration-vs-pnl.png`,
  },

  entryHour: {
    title: "Performance par heure d’entrée",
    intro:
      "Résultats obtenus en fonction de l’heure à laquelle les trades ont été ouverts.",
    meaning:
      "Ce graphique aide à identifier les heures où vos prises de position sont les plus efficaces et celles où elles génèrent davantage de pertes.",
    howTo:
      "Chaque barre correspond à une heure d’entrée et affiche le résultat moyen par trade sur cette heure, pas le cumul : une heure très tradée n’écrase donc pas les autres. Les barres vertes représentent une moyenne positive et les rouges une moyenne négative. Une grande barre positive indique une heure historiquement favorable, mais il est important de vérifier que le nombre de trades est suffisamment représentatif — il figure dans l’infobulle, avec le cumul.",
    image: `${IMG}/entry-hour.png`,
  },

  weekdayPerf: {
    title: "Performance par jour de la semaine",
    intro:
      "Comparaison de vos résultats entre les différents jours de la semaine.",
    meaning:
      "Ce graphique permet de découvrir les jours pendant lesquels votre stratégie ou votre comportement produit les meilleurs résultats.",
    howTo:
      "Chaque barre correspond à un jour. Une barre verte indique une performance positive et une barre rouge une performance négative. Plus la barre est longue, plus le résultat cumulé est important. Analysez également le nombre de trades réalisés chaque jour avant de tirer une conclusion.",
    image: `${IMG}/weekday-perf.png`,
  },

  tagPerf: {
    title: "Performance par stratégie / tag",
    intro:
      "Comparaison des résultats obtenus avec vos différentes stratégies ou catégories de trades.",
    meaning:
      "Les tags permettent de regrouper les trades selon une stratégie, un setup, une configuration de marché ou une règle particulière. Ce graphique aide à identifier les approches qui fonctionnent réellement. C’est à vous d’ajouter les tags depuis l’onglet Journal.",
    howTo:
      "Chaque barre correspond à une stratégie ou à un tag utilisé dans votre journal. Les valeurs positives représentent les stratégies rentables et les valeurs négatives les stratégies déficitaires. Le graphique reste vide lorsque les trades ne possèdent aucun tag ou lorsque les données disponibles sont insuffisantes. Une stratégie doit être évaluée avec son nombre de trades, son taux de réussite et son risque moyen.",
    image: null,
  },
};

const en = {
  pnlCurve: {
    title: "P&L curve (closed)",
    intro:
      "Cumulative result of your closed trades, in closing order.",
    meaning:
      "The curve shows the account trajectory rather than individual trades. It answers one question: is capital growing, and at what cost?",
    howTo:
      "Plateaus are steady runs, vertical drops are concentrated losses. The period selector at the top right narrows the view to the last week, the last month, or the last 3, 6 or 12 months.",
    image: null,
  },

  scoreRadar: {
    title: "Score radar",
    intro:
      "A synthetic view of your overall performance quality across several key indicators.",
    meaning:
      "Each axis is one dimension of your trading: win rate, profit factor, expectancy, recovery, drawdown or result streaks. The chart makes strengths and weak spots obvious at a glance.",
    howTo:
      "The further the coloured area reaches out along an axis, the better your score on that indicator. A wide, balanced shape usually means steadier, more solid performance. An area hugging the centre points to an indicator that is still fragile.",
    image: `${IMG}/score-radar.png`,
  },

  tracker: {
    title: "Progress tracker",
    intro:
      "A visual record of your consistency and results across days and weeks.",
    meaning:
      "Each cell is one day. Colours separate positive days, negative days and days without activity at a glance.",
    howTo:
      "Read left to right for weeks, top to bottom for weekdays. Green cells are positive days, red ones negative, empty cells mean no data or no trade. The goal is to read consistency, not a single isolated day.",
    image: `${IMG}/progress-tracker.png`,
  },

  avgWinLoss: {
    title: "Average wins / losses",
    intro:
      "Comparison between the average amount won on winning trades and the average amount lost on losing trades.",
    meaning:
      "This chart tells you whether your average wins offset your average losses well enough. The line also tracks how the win rate evolves over the period.",
    howTo:
      "Green bars are average wins, red bars average losses. The line reads against the percentage axis on the right. A strategy can stay profitable with a low win rate when average wins are clearly larger than average losses.",
    image: `${IMG}/avg-win-loss.png`,
  },

  winLossSplit: {
    title: "Wins / losses split",
    intro: "How all your trades split between winners and losers.",
    meaning:
      "The chart shows your win rate — the share of closed trades with a positive result.",
    howTo:
      "The green part is winning trades, the red part losing trades. The percentage in the middle is the win rate. Always read it alongside average win and average loss: a high win rate does not guarantee a profitable strategy.",
    image: `${IMG}/win-loss-split.png`,
  },

  monthlyStats: {
    title: "Monthly stats",
    intro: "How your results and your trading activity evolve month by month.",
    meaning:
      "This chart compares performance across months and shows how it relates to the number of trades taken.",
    howTo:
      "Bars are each month's net result, the line is the matching trade volume. Use it to tell whether more activity actually improves performance or simply produces more mistakes.",
    image: `${IMG}/monthly-stats.png`,
  },

  resultDistribution: {
    title: "Result distribution (per trade)",
    intro: "How your trades spread across the amount won or lost on each one.",
    meaning:
      "The chart shows which result levels come up most often — repeated small losses, usual gains, and outliers.",
    howTo:
      "The horizontal axis is the win/loss level, the vertical axis the number of trades in it. Red bars are negative results, green ones positive. A tall concentration on one value means many trades produce a similar outcome.",
    image: `${IMG}/result-distribution.png`,
  },

  pnlDayHour: {
    title: "P&L by day & hour",
    intro:
      "Heatmap of your performance by weekday and trading hour.",
    meaning:
      "It shows the time slots where you get your best and your worst results.",
    howTo:
      "Each cell is the average performance for one day and one hour. Positive colours mark profitable slots, negative colours weaker periods. The more intense the colour, the larger the average result.",
    image: `${IMG}/pnl-day-hour.png`,
  },

  durationVsPnl: {
    title: "Trade duration vs performance",
    intro:
      "Relationship between how long you hold a position and its financial result.",
    meaning:
      "This chart tells you whether your short, medium or long trades tend to perform best. It also helps spot positions held too long.",
    howTo:
      "Each dot is one trade: horizontal position is duration, vertical position is profit or loss. Dots above zero are winners, below zero losers. The r coefficient measures how strong the duration/result link is — near zero means little correlation.",
    image: `${IMG}/duration-vs-pnl.png`,
  },

  entryHour: {
    title: "Entry hour performance",
    intro: "Results broken down by the hour at which trades were opened.",
    meaning:
      "This chart helps identify the hours where your entries work best and those where they generate more losses.",
    howTo:
      "Each bar is one entry hour and shows the average result per trade for that hour, not the total — so a heavily traded hour does not drown out the rest. Green bars are a positive average, red ones negative. A tall green bar marks a historically favourable hour — but check the trade count is large enough to mean anything; it sits in the tooltip, along with the cumulative total.",
    image: `${IMG}/entry-hour.png`,
  },

  weekdayPerf: {
    title: "Weekday performance",
    intro: "Comparison of your results across the days of the week.",
    meaning:
      "This chart reveals the days on which your strategy — or your behaviour — produces the best results.",
    howTo:
      "Each bar is one day. Green means positive performance, red negative. The longer the bar, the larger the cumulative result. Also check how many trades each day holds before drawing a conclusion.",
    image: `${IMG}/weekday-perf.png`,
  },

  tagPerf: {
    title: "Performance by strategy / tag",
    intro:
      "Comparison of the results obtained with your different strategies or trade categories.",
    meaning:
      "Tags group trades by strategy, setup, market condition or a specific rule. This chart shows which approaches actually work. Tags are yours to add, from the Journal tab.",
    howTo:
      "Each bar is one strategy or tag used in your journal. Positive values are profitable strategies, negative ones losing strategies. The chart stays empty when trades carry no tag or the data is too thin. Judge a strategy together with its trade count, win rate and average risk.",
    image: null,
  },
};

// Intertitres de la fiche, et libellé du bouton qui l'ouvre.
const LABELS = {
  fr: {
    meaning: "Ce que cela signifie",
    howTo: "Comment le lire",
    cta: "Cliquer pour info",
    ctaShort: "Infos",
    ctaTitle: "Cliquer pour voir l’explication de ce graphique",
  },
  en: {
    meaning: "What it means",
    howTo: "How to read it",
    cta: "Click for info",
    ctaShort: "Info",
    ctaTitle: "Click to see how to read this chart",
  },
};

const CHART_HELP = { fr, en };

/** Renvoie la fiche d'aide d'un graphique dans la langue courante. */
export function getChartHelp(key, language = "fr") {
  const isEn = String(language).startsWith("en");
  const entry = (isEn ? en : fr)[key];
  if (!entry) return null;
  return { ...entry, labels: isEn ? LABELS.en : LABELS.fr };
}

export default CHART_HELP;
